import { useState, useEffect, useCallback, useRef } from 'react'

export type PaymentStatus = 'IDLE' | 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'CANCELLED' | 'TIMEOUT'

export interface PaymentNotification {
  orderRef: string
  status: PaymentStatus
  message: string
  amount?: number
  transactionRef?: string
  timestamp: string
}

interface UsePaymentWebSocketOptions {
  onStatusChange?: (notification: PaymentNotification) => void
  onComplete?: (notification: PaymentNotification) => void
  onError?: (notification: PaymentNotification) => void
  autoReconnect?: boolean
  reconnectAttempts?: number
  reconnectInterval?: number
}

const getWebSocketUrl = () => {
  const apiUrl = (import.meta as any).env.VITE_API_BASE_URL || 'http://localhost:5001/api/v1'
  try {
    const url = new URL(apiUrl)
    const protocol = url.protocol === 'https:' ? 'wss:' : 'ws:'
    const host = url.host
    return `${protocol}//${host}/api/v1/notification`
  } catch {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    return `${protocol}//localhost:5001/api/v1/notification`
  }
}

export function usePaymentWebSocket(options: UsePaymentWebSocketOptions = {}) {
  const {
    onStatusChange,
    onComplete,
    onError,
    autoReconnect = true,
    reconnectAttempts = 3,
    reconnectInterval = 2000,
  } = options

  const [status, setStatus] = useState<PaymentStatus>('IDLE')
  const [message, setMessage] = useState<string>('')
  const [isConnected, setIsConnected] = useState(false)
  const [isConnecting, setIsConnecting] = useState(false)
  const [notification, setNotification] = useState<PaymentNotification | null>(null)

  const wsRef = useRef<WebSocket | null>(null)
  const orderRefRef = useRef<string | null>(null)
  const reconnectCountRef = useRef(0)
  const heartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Cleanup function
  const cleanup = useCallback(() => {
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current)
      heartbeatRef.current = null
    }
    if (wsRef.current) {
      wsRef.current.close()
      wsRef.current = null
    }
    setIsConnected(false)
    setIsConnecting(false)
  }, [])

  // Connect to WebSocket
  const connect = useCallback((orderRef: string) => {
    // Clean up existing connection
    cleanup()

    orderRefRef.current = orderRef
    setIsConnecting(true)
    setStatus('PENDING')
    setMessage('Connecting to payment status service...')
    reconnectCountRef.current = 0

    const wsUrl = `${getWebSocketUrl()}/${orderRef}`
    console.log('Connecting to WebSocket:', wsUrl)

    try {
      const ws = new WebSocket(wsUrl)
      wsRef.current = ws

      ws.onopen = () => {
        console.log('WebSocket connected for orderRef:', orderRef)
        setIsConnected(true)
        setIsConnecting(false)
        reconnectCountRef.current = 0

        // Start heartbeat
        heartbeatRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }))
          }
        }, 25000)
      }

      ws.onmessage = (event) => {
        try {
          const data: PaymentNotification = JSON.parse(event.data)
          console.log('WebSocket message received:', data)

          // Ignore pong messages
          if ((data as any).type === 'pong') return

          setNotification(data)
          setStatus(data.status as PaymentStatus)
          setMessage(data.message)

          // Call status change callback
          onStatusChange?.(data)

          // Call specific callbacks based on status
          if (data.status === 'COMPLETED') {
            onComplete?.(data)
            // Keep connection open for a bit to ensure message is displayed
            setTimeout(() => cleanup(), 5000)
          } else if (data.status === 'FAILED' || data.status === 'CANCELLED') {
            onError?.(data)
            setTimeout(() => cleanup(), 5000)
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error)
        }
      }

      ws.onclose = (event) => {
        console.log('WebSocket closed:', event.code, event.reason)
        setIsConnected(false)

        // Clear heartbeat
        if (heartbeatRef.current) {
          clearInterval(heartbeatRef.current)
          heartbeatRef.current = null
        }

        // Attempt to reconnect if not intentionally closed and status is still pending
        if (
          autoReconnect &&
          event.code !== 1000 &&
          reconnectCountRef.current < reconnectAttempts &&
          orderRefRef.current &&
          (status === 'PENDING' || status === 'PROCESSING')
        ) {
          reconnectCountRef.current++
          console.log(`Attempting to reconnect (${reconnectCountRef.current}/${reconnectAttempts})...`)
          setTimeout(() => {
            if (orderRefRef.current) {
              connect(orderRefRef.current)
            }
          }, reconnectInterval)
        }
      }

      ws.onerror = (error) => {
        console.error('WebSocket error:', error)
        setIsConnecting(false)
      }
    } catch (error) {
      console.error('Failed to create WebSocket:', error)
      setIsConnecting(false)
      setStatus('FAILED')
      setMessage('Failed to connect to payment status service')
    }
  }, [cleanup, autoReconnect, reconnectAttempts, reconnectInterval, onStatusChange, onComplete, onError, status])

  // Disconnect
  const disconnect = useCallback(() => {
    orderRefRef.current = null
    cleanup()
    setStatus('IDLE')
    setMessage('')
    setNotification(null)
  }, [cleanup])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup()
    }
  }, [cleanup])

  return {
    status,
    message,
    isConnected,
    isConnecting,
    notification,
    connect,
    disconnect,
  }
}

export default usePaymentWebSocket
